<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('organizationsPage.pageTitle') }}
    </v-toolbar>
    <v-sheet color="grey lighten-3"
             class="px-4 pt-4 pb-3"
             tile>
      <h3 class="transition-swing font-weight-medium">{{ $t('organizationsPage.filterTitle') }}</h3>
      <div class="px-4">
        <v-row>
          <v-col cols="12" sm="3" md="3" lg="3" class="">
            <v-autocomplete v-model="filters.unit"
                            :items="units"
                            :label="$t('organizationsPage.labels.branch')"
                            item-text="name"
                            item-value="id"
                            :clearable="true"
                            @change="load"
            />
          </v-col>
          <v-col cols="12" sm="3" md="3" lg="3">
            <v-select v-model="filters.isActive"
                      :items="statusOptions"
                      :clearable="true"
                      :label="$t('tableHeaders3.status')"
                      @change="load"
            >
            </v-select>
          </v-col>
          <v-col cols="12" sm="3" md="3" lg="3">
            <v-text-field
                :label="$t('tableHeaders3.tin')"
                v-model="filters.inn"
                :clearable="true"
                @change="load"
            />
          </v-col>
          <v-col cols="12" sm="12" md="12" lg="12">
            <div class="d-flex justify-space-between">
              <v-btn color="primary" dark @click="load">{{ $t('common.actions.update') }}</v-btn>
              <v-btn color="primary" @click="onAddOrganizationButtonClicked">{{ $t('organizationsPage.addBtn') }}</v-btn>
            </div>
            <v-dialog v-model="addOrganizationDialog" :persistent="true" width="600px">
              <AddOrganizationComponent @cancel="onAddOrganizationCanceled" @added="onAddOrganizationSuccess"/>
            </v-dialog>
          </v-col>
        </v-row>
      </div>
    </v-sheet>
    <v-data-table
        :items="organizations"
        :server-items-length="total"
        :headers="headers"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, 200] }"
    >
      <template v-slot:item.createdOn="{item}">
        {{ item.createdOn.format('DD.MM.YYYY HH:mm:ss') }}
      </template>

      <template v-slot:item.contractDateFrom="{item}">
        <template v-if="item.contractDateFrom">
          {{ item.contractDateFrom.format('DD.MM.YYYY') }}
        </template>
        <template v-else>
          <v-icon>mdi-calendar-remove</v-icon>
        </template>
      </template>

      <template v-slot:item.contractDateTo="{item}">
        <template v-if="item.contractDateTo">
          {{ item.contractDateTo.format('DD.MM.YYYY') }}
        </template>
        <template v-else>
          <v-icon>mdi-calendar-remove</v-icon>
        </template>
      </template>

      <template v-slot:item.isActive="{item}">
        <v-icon v-if="item.isActive">mdi-check-outline</v-icon>
        <v-icon v-else>mdi-lock-alert</v-icon>
      </template>

      <template v-slot:item.actions="{item}">
        <div class="d-flex justify-end">
          <v-btn :disabled="canEditOrganization(item)" icon @click="onEditOrganizationButtonClicked(item)" :title="$t('organizationsPage.tooltips.edit')">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-if="$store.getters.isAdmin" icon @click="onDeleteItemButtonClicked(item)" :title="$t('organizationsPage.tooltips.delete')">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn icon small :to="{name: 'admin-users', query: {inn: item.inn}}" :title="$t('globalOrganizationsPage.tooltips.users')">
            <v-icon>mdi-account-group</v-icon>
          </v-btn>
          <v-btn icon small :to="{name: 'admin-units', query: {inn: item.inn}}" :title="$t('globalOrganizationsPage.tooltips.departments')">
            <v-icon>mdi-office-building</v-icon>
          </v-btn>
        </div>
      </template>

    </v-data-table>
    <v-dialog v-model="editOrganizationDialog" :persistent="true" width="600px">
      <EditOrganizationComponent ref="editOrganization" @cancel="onEditOrganizationCanceled"
                                 @updated="onEditOrganizationSuccess"/>
    </v-dialog>
    <v-dialog v-model="deleteItemDialog" :persistent="true" max-width="700px">
      <v-card>
        <v-card-title class="headline justify-center">
          <template v-if="currentItem">
            <p>{{ $t('organizationsPage.deleteDialog.confirmTitle') }}</p>
            <p>{{ $t('organizationsPage.deleteDialog.nameLabel') }}: {{ this.currentItem.name }} ({{ this.currentItem.inn }})</p>
          </template>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDeleteItemDialog">{{ $t('common.actions.cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click="deleteItemConfirm">{{ $t('organizationsPage.deleteDialog.deleteBtn') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <template>
      <template v-if="isEditOrganizationSuccess">
        <v-snackbar v-model="isEditOrganizationSuccess"
                    :timeout="5000"
                    :top="true"
                    color="success"
        >
          {{ $t('organizationsPage.messages.editSuccess') }}
        </v-snackbar>
      </template>
      <template v-else>
        <v-snackbar v-model="isEditOrganizationSuccess"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('organizationsPage.messages.editError') }}
        </v-snackbar>
      </template>
    </template>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP, LAYOUT_TITLE_CHANGED} from "@/store/modules/layout/actions";
import OrganizationService from "@/services/organization.service";
import _ from 'underscore'
import Mappers from '@/mappers'
import AddOrganizationComponent from "../../components/organization/AddOrganizationComponent";
import EditOrganizationComponent from "../../components/organization/EditOrganizationComponent";
import GlobalUnitService from "@/services/global.unit.service";
import {Admin, Moderator} from "@/enums/user.type";

export default {
  name: "OrganizationsPage",
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 50,
      },
      organizations: [],
      units: [],
      total: 0,

      addOrganizationDialog: false,
      isAddOrganizationSuccess: false,
      editOrganizationDialog: false,
      isEditOrganizationSuccess: false,

      filters: {
        inn: null,
        unit: null,
        start: null,
        end: null,
        isActive: false
      },

      currentItem: null,
      deleteItemDialog: false,
      isDeleteItemSuccess: false
    }
  },
  components: {
    AddOrganizationComponent,
    EditOrganizationComponent
  },
  computed: {
    statusOptions() {
      return [
        { text: this.$t('organizationsPage.statusOptions.active'), value: true },
        { text: this.$t('organizationsPage.statusOptions.inactive'), value: false },
      ]
    },
    headers: function () {
      const headers = []

      headers.push({
        text: this.$t('tableHeaders3.number'),
        align: 'center',
        sortable: false,
        value: 'displayNumber'
      })

      headers.push({
        text: this.$t('tableHeaders3.name'),
        align: 'left',
        sortable: false,
        value: 'name'
      })

      headers.push({
        text: this.$t('tableHeaders3.tin'),
        align: 'center',
        sortable: false,
        value: 'inn'
      })

      headers.push({
        text: this.$t('tableHeaders3.address'),
        align: 'left',
        sortable: false,
        value: 'address'
      })

      headers.push({
        text: this.$t('tableHeaders3.registrationDate'),
        align: 'center',
        sortable: false,
        value: 'createdOn'
      })

      headers.push({
        text: this.$t('tableHeaders3.contractNumber'),
        align: 'center',
        sortable: false,
        value: 'contractNumber'
      })

      headers.push({
        text: this.$t('tableHeaders3.contractDateFrom'),
        align: 'center',
        sortable: false,
        value: 'contractDateFrom'
      })

      headers.push({
        text: this.$t('tableHeaders3.contractDateTo'),
        align: 'center',
        sortable: false,
        value: 'contractDateTo'
      })

      headers.push({
        text: this.$t('tableHeaders3.contractSignerBranch'),
        align: 'center',
        sortable: false,
        value: 'contractSignerUnit'
      })

      headers.push({
        text: this.$t('tableHeaders3.mailsCount'),
        align: 'center',
        sortable: false,
        value: 'mailsCount'
      })

      headers.push({
        text: this.$t('tableHeaders3.status'),
        align: 'center',
        sortable: false,
        value: 'isActive'
      })


      headers.push({
        text: '',
        align: 'center',
        sortable: false,
        value: 'actions'
      })

      return headers
    },
    items() {
      let number = 1;
      let skip = this.skip;
      return _.map(this.users, u => {
        u.displayNumber = skip + (number++)
        return u
      })

    },
    skip() {
      return (this.options.page - 1) * this.options.itemsPerPage
    }
  },
  methods: {
    canEditOrganization(id){
      if(this.$store.getters.appLogin.type === Admin){
        return false;
      }
      else if(this.$store.getters.appLogin.type === Moderator){
        if(this.$store.getters.appLogin.isHead){
          return false
        }
        else if(this.$store.getters.appLogin.globalunit.id === id.contractSignerUnitId){
          return false
        }
        else {
          return true
        }
      }
      return true
    },
    load() {
      this.$store.dispatch(LAYOUT_LOADING_START)
      const {page, itemsPerPage} = this.options;
      const skip = (page - 1) * itemsPerPage;

      OrganizationService.getOrganizations({unitId: this.filters.unit, inn: this.filters.inn, skip: skip, limit: itemsPerPage, isActive: this.filters.isActive})
          .then(response => {
            let displayNumber = 0
            this.organizations = _.map(response.data.Records, o => {
              let org = Mappers.mapOrganization(o?.Organization)
              org.mailsCount = o?.CreatedMailsCount + " | " + o?.SentMailsCount
              org.displayNumber = ++displayNumber

              return org
            })
            this.total = response.data.Total
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadUnits() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      return GlobalUnitService.getUnits({inn: this.$store.getters.inn})
          .then(response => {
            let displayNumber = 0
            this.units = _.map(response.data, u => {
              let unit = Mappers.mapGlobalUnit(u)
              unit.displayNumber = ++displayNumber

              return unit
            })
            this.total = this.units.length
            // if (this.units.length) {
            //   if (this.$store.getters.appLogin.unit && this.$store.getters.appLogin.unit.isHead) {
            //     this.filters.unit = this.$store.getters.appLogin.unit.id;
            //   } else {
            //     if (this.$store.getters.appLogin.globalunit) {
            //       this.filters.unit = this.$store.getters.appLogin.globalunit.id;
            //     } else {
            //       this.filters.unit = null;
            //     }
            //   }
            // }
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    onAddOrganizationButtonClicked() {
      this.addOrganizationDialog = true
    },
    onAddOrganizationSuccess() {
      this.isAddOrganizationSuccess = true
      this.addOrganizationDialog = false
      this.load()
    },
    onAddOrganizationCanceled() {
      this.addOrganizationDialog = false
    },
    onEditOrganizationButtonClicked(organization) {
      this.editOrganizationDialog = true

      this.$nextTick(() => {
        this.$refs.editOrganization.set(organization)
      })
    },
    onEditOrganizationCanceled() {
      this.editOrganizationDialog = false
    },
    onEditOrganizationSuccess() {
      this.isEditOrganizationSuccess = true
      this.editOrganizationDialog = false
      this.load()
    },
    onDeleteItemButtonClicked(item) {
      this.currentItem = item;
      this.deleteItemDialog = true
    },
    deleteItemConfirm() {
      this.deleteOrganization(this.currentItem.id)
      this.currentItem = null
      this.deleteItemDialog = false
    },
    closeDeleteItemDialog() {
      this.deleteItemDialog = false
    },
    deleteOrganization(id) {
      OrganizationService.delete(id)
          .then(() => {
            this.isDeleteItemSuccess = true
          })
      this.loadUnits()
          .then(() => {
            this.load()
          })
    },
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('organizationsPage.pageTitle'))

    this.loadUnits()
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.load()
      }
    }
  }
}
</script>

<style scoped>

</style>